import { useId, useRef } from 'react'

interface SegmentedOption<T extends string> {
  value: T
  label: string
}

interface SegmentedProps<T extends string> {
  label: string
  options: SegmentedOption<T>[]
  value: T
  onChange: (value: T) => void
}

/**
 * A segmented control — two to four mutually exclusive choices, all visible.
 *
 * It is a `radiogroup` with roving tabstop, not a row of buttons: arrow keys
 * move between segments and select as they go, Home/End jump to the ends, and
 * exactly one segment is in the tab order. A row of independent buttons looks
 * identical and behaves wrongly for anyone not using a touchscreen.
 *
 * Selection is ink-filled rather than accent-filled, for the same reason as
 * `Chip`: the accent is reserved.
 *
 * SUAP-0103.
 */
export function Segmented<T extends string>({ label, options, value, onChange }: SegmentedProps<T>) {
  const id = useId()
  const refs = useRef<(HTMLButtonElement | null)[]>([])

  function move(from: number, delta: number) {
    const next = (from + delta + options.length) % options.length
    const option = options[next]
    if (option === undefined) return
    onChange(option.value)
    refs.current[next]?.focus()
  }

  return (
    <div className="flex flex-col gap-1.5">
      <span id={id} className="text-sm font-medium text-ink">
        {label}
      </span>
      <div
        role="radiogroup"
        aria-labelledby={id}
        className="flex gap-1 rounded-input border border-hairline bg-surface p-1"
      >
        {options.map((option, index) => {
          const selected = option.value === value
          return (
            <button
              key={option.value}
              ref={(node) => {
                refs.current[index] = node
              }}
              type="button"
              role="radio"
              aria-checked={selected}
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(option.value)}
              onKeyDown={(event) => {
                if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                  event.preventDefault()
                  move(index, 1)
                } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                  event.preventDefault()
                  move(index, -1)
                } else if (event.key === 'Home') {
                  event.preventDefault()
                  move(0, 0)
                } else if (event.key === 'End') {
                  event.preventDefault()
                  move(options.length - 1, 0)
                }
              }}
              className={[
                'min-h-11 flex-1 truncate rounded-input px-3 text-sm font-medium',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink',
                'motion-safe:transition-[background-color,border-color,color]',
                selected ? 'bg-ink text-ground' : 'text-ink-soft',
              ].join(' ')}
            >
              {option.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}
